import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import MachineDetails from './MachineDetails';
import {
  ACCOUNT_STATE_ORDER,
  getOvereenkomstLabel,
  AccountStateBadge,
} from './accountsShared';
import { TODAY_STATUS_LABELS, TODAY_STATUS_ORDER, normalizeTodayStatus } from './openDays';
import './MachineManager.css'; // Import CSS file
import { useSearchParams } from 'react-router-dom';

// DJB-182: per-device column customisation. Name and Plan (Overeenkomst)
// are always shown; every other column — Seen included since DJB-189,
// Province since DJB-206, State since DJB-254 — can be hidden through the
// "Customize Table" panel. Listed in table order.
export const HIDEABLE_COLUMNS = [
  { key: 'seen', label: 'Seen' },
  { key: 'street', label: 'Street' },
  { key: 'postalcode', label: 'Post' },
  { key: 'city', label: 'Place' },
  { key: 'province', label: 'Province' },
  { key: 'state', label: 'State' },
  { key: 'today', label: 'Today' },
  { key: 'id', label: 'ID' },
  { key: 'software', label: 'Software' },
  { key: 'type', label: 'Model' },
  { key: 'currentCredits', label: 'Credits' },
  { key: 'tokensLeft', label: 'Tokens' },
  { key: 'songsRequested', label: 'Songs' },
  { key: 'cash', label: 'Cash' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'revenue', label: 'Total' },
  { key: 'daysSinceClear', label: 'Days' },
  { key: 'avgPerDay', label: 'Avg' },
];

// DJB-189: a fresh device shows only Seen, Name, Street, Post, Place, Plan
// and State — every other column starts out hidden. DJB-206 adds Province to
// that visible default; a device with a stored hidden set picks it up too,
// since only keys listed in storage are hidden. DJB-253's Today column joins
// the visible default the same way.
export const DEFAULT_HIDDEN_COLUMNS = [
  'id', 'software', 'type', 'currentCredits', 'tokensLeft', 'songsRequested',
  'cash', 'mobile', 'revenue', 'daysSinceClear', 'avgPerDay',
];

export const HIDDEN_COLUMNS_STORAGE_KEY = 'accountsInfo.hiddenColumns';

// localStorage access is wrapped in try/catch: Safari throws in some private
// modes and when storage is disabled — the table then just runs on defaults
const readStoredHiddenColumns = () => {
  try {
    const raw = window.localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_HIDDEN_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_HIDDEN_COLUMNS;
    const known = new Set(HIDEABLE_COLUMNS.map((column) => column.key));
    return parsed.filter((key) => known.has(key));
  } catch (e) {
    return DEFAULT_HIDDEN_COLUMNS;
  }
};

const writeStoredHiddenColumns = (hiddenColumns) => {
  try {
    window.localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(hiddenColumns));
  } catch (e) {
    // Storage unavailable — the toggle still applies for this session
  }
};

// Same set as MachineDetails: only these overeenkomst types have a token
// balance to fetch for the Tokens column
const PAID_OVEREENKOMST_TYPES = ['BASIC', 'PRO', 'UNLIMITED'];

const MachineManager = () => {
  const { machines, loading, error, fetchTokenBalance } = useContext(MachineContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [fleetFilter, setFleetFilter] = useState('in-fleet');
  // DJB-253: 'all' or one of the server-side todayStatus values
  const [todayFilter, setTodayFilter] = useState('all');
  // DJB-182: hidden column keys, seeded from this device's stored choice
  const [hiddenColumns, setHiddenColumns] = useState(readStoredHiddenColumns);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const customizeRef = useRef(null);
  // Token balances live behind a per-machine endpoint, so the Tokens
  // column fetches lazily (only while visible) and caches per machine id
  const [tokenBalances, setTokenBalances] = useState({});
  const requestedTokenIds = useRef(new Set());
  // DJB-130: the selected row's account renders inline below the table (the
  // space the stat widgets freed up when they moved to the Dashboard, DJB-128)
  const [selectedId, setSelectedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Old Detail-view deep links (?view=details&id=... / ?id=...) degrade to
  // this page with the linked account preselected
  useEffect(() => {
    const machineId = searchParams.get('id');
    if (machineId && machines.some(m => m.id === machineId)) {
      setSelectedId(machineId);
    }
  }, [searchParams, machines]);

  // DJB-189: the side-rail Settings menu deep-links here with
  // ?customize=table to open the Customize Table panel; the marker param is
  // consumed (stripped) so the panel can be reopened from the menu after
  // closing and a reload doesn't pop it open again
  useEffect(() => {
    if (searchParams.get('customize') !== 'table') return;
    setCustomizeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('customize');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedMachine = useMemo(
    () => machines.find(m => m.id === selectedId) || null,
    [machines, selectedId]
  );

  const isColumnVisible = (key) => !hiddenColumns.includes(key);

  const toggleColumn = (key) => {
    const next = hiddenColumns.includes(key)
      ? hiddenColumns.filter((k) => k !== key)
      : [...hiddenColumns, key];
    setHiddenColumns(next);
    writeStoredHiddenColumns(next);
  };

  const resetColumns = () => {
    setHiddenColumns(DEFAULT_HIDDEN_COLUMNS);
    try {
      window.localStorage.removeItem(HIDDEN_COLUMNS_STORAGE_KEY);
    } catch (e) {
      // Storage unavailable — the reset still applies for this session
    }
  };

  // Close the customize panel on outside click or Escape
  useEffect(() => {
    if (!customizeOpen) return undefined;
    const onMouseDown = (event) => {
      if (customizeRef.current && !customizeRef.current.contains(event.target)) {
        setCustomizeOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCustomizeOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [customizeOpen]);

  // Fetch token balances only while the Tokens column is visible, once
  // per machine (paid overeenkomsten only — others have no balance)
  useEffect(() => {
    if (hiddenColumns.includes('tokensLeft') || !fetchTokenBalance) return;
    machines.forEach((machine) => {
      if (!PAID_OVEREENKOMST_TYPES.includes(machine.overeenkomst)) return;
      if (requestedTokenIds.current.has(machine.id)) return;
      requestedTokenIds.current.add(machine.id);
      fetchTokenBalance(machine.id).then((balance) => {
        setTokenBalances((prev) => ({ ...prev, [machine.id]: balance ?? 'N/A' }));
      });
    });
  }, [hiddenColumns, machines, fetchTokenBalance]);

  const getTokensLeft = (machine) => {
    if (!PAID_OVEREENKOMST_TYPES.includes(machine.overeenkomst)) return 'N/A';
    return tokenBalances[machine.id] ?? '…';
  };

  const calculateDaysOffline = (lastSeen) => {
    if (!lastSeen || lastSeen.length < 3) return 'N/A';
    const lastSeenDate = new Date(lastSeen[0], lastSeen[1] - 1, lastSeen[2]);
    const today = new Date();
    const diffTime = Math.abs(today - lastSeenDate);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const getSeenStatus = (lastSeen) => {
    if (!lastSeen || lastSeen.length < 6) return 'never';
    const lastSeenDate = new Date(
      lastSeen[0], lastSeen[1] - 1, lastSeen[2], lastSeen[3], lastSeen[4], lastSeen[5]
    );
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / (1000 * 60);
    return diffMinutes <= 60 ? 'online' : 'offline';
  };

  const getStatusIndicator = (lastSeen) => {
    const status = getSeenStatus(lastSeen);
    return status === 'online' ? '🟢' : status === 'offline' ? '🔴' : '⚪';
  };

  const calculateRevenue = (machine) => {
    if (machine.currentCoinDeposit === undefined || machine.currentIncomeFromPayconiq === undefined) return 'N/A';
    return ((machine.currentCoinDeposit + machine.currentIncomeFromPayconiq) / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const calculateCash = (machine) => {
    if (machine.currentCoinDeposit === undefined) return 'N/A';
    return (machine.currentCoinDeposit / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const calculateMobile = (machine) => {
    if (machine.currentIncomeFromPayconiq === undefined) return 'N/A';
    return (machine.currentIncomeFromPayconiq / 100).toFixed(2).replace('.', ',') + ' €';
  };

  const calculateDaysSinceClear = (coinsLastEmptied) => {
    if (!coinsLastEmptied || coinsLastEmptied.length < 3) return 'N/A';
    const lastClearDate = new Date(coinsLastEmptied[0], coinsLastEmptied[1] - 1, coinsLastEmptied[2]);
    const today = new Date();
    const diffTime = Math.abs(today - lastClearDate);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateAveragePerDay = (machine) => {
    const days = calculateDaysSinceClear(machine.coinsLastEmptied);
    const totalIncome = (machine.currentCoinDeposit || 0) + (machine.currentIncomeFromPayconiq || 0);
    
    if (days === 'N/A' || days === 0) return 'N/A';
    
    return ((totalIncome / 100) / days).toFixed(2).replace('.', ',') + ' €';
  };

  const fleetFilteredMachines = useMemo(() => {
    let result = machines;
    if (fleetFilter === 'in-fleet') {
      result = result.filter(m => (m.fleetState || '').toUpperCase() === 'IN_FLEET');
    } else if (fleetFilter === 'out-fleet') {
      result = result.filter(m => (m.fleetState || '').toUpperCase() === 'OUT_FLEET');
    }
    // DJB-253: "today" is decided by the backend (Europe/Brussels); the
    // filter only reads todayStatus, it never recomputes it from openDays
    if (todayFilter !== 'all') {
      result = result.filter(m => normalizeTodayStatus(m.todayStatus) === todayFilter);
    }
    return result;
  }, [machines, fleetFilter, todayFilter]);

  // DJB-184: money cells format with a decimal comma ("1,50 €"), which
  // parseFloat truncates at the comma; sort on the full numeric value instead.
  // N/A sorts as the lowest amount so those rows collect at one end.
  const parseEuroForSort = (formatted) =>
    formatted === 'N/A' ? Number.MIN_SAFE_INTEGER : parseFloat(String(formatted).replace(',', '.'));

  // DJB-184: N/A day counts used to leak a string into the numeric compare and
  // crash on number.localeCompare; never-seen sorts as the oldest instead.
  const parseDaysForSort = (days) => (days === 'N/A' ? Number.MAX_SAFE_INTEGER : days);

  // DJB-206: Tokens renders 'N/A' (no paid plan) or '…' (balance still
  // loading) besides the numeric balance; both sort as the lowest balance so
  // they collect at one end instead of leaking a string into the compare.
  const parseTokensForSort = (tokens) =>
    typeof tokens === 'number' ? tokens : Number.MIN_SAFE_INTEGER;

  // One place resolving the value a column sorts on; numbers compare
  // numerically, everything else falls through to a string compare.
  const getSortValue = (machine, key) => {
    switch (key) {
      case 'daysOffline': return parseDaysForSort(calculateDaysOffline(machine.lastSeen));
      case 'daysSinceClear': return parseDaysForSort(calculateDaysSinceClear(machine.coinsLastEmptied));
      case 'revenue': return parseEuroForSort(calculateRevenue(machine));
      case 'cash': return parseEuroForSort(calculateCash(machine));
      case 'mobile': return parseEuroForSort(calculateMobile(machine));
      case 'avgPerDay': return parseEuroForSort(calculateAveragePerDay(machine));
      case 'overeenkomst': return getOvereenkomstLabel(machine.overeenkomst);
      // DJB-253: Open → Closed → Unknown ascending
      case 'todayStatus': return TODAY_STATUS_ORDER.indexOf(normalizeTodayStatus(machine.todayStatus));
      // DJB-206: the six DJB-182 columns and Province become sortable; the
      // counts sort on the same fallback the cells render (missing → 0)
      case 'software': return machine.softwareVersion || '';
      case 'currentCredits': return machine.currentCredits || 0;
      case 'tokensLeft': return parseTokensForSort(getTokensLeft(machine));
      case 'songsRequested': return machine.songsRequestedSince || 0;
      default: return machine[key] ?? '';
    }
  };

  // DJB-206: software versions ("2.10.0" after "2.9.0") and province names in
  // mixed casing ("ANTWERPEN" next to "Antwerpen") order naturally with
  // numeric, base-sensitivity collation — same options MachineGroups uses.
  // Machines without a version ('' → shown as Unknown) sort to the end.
  const NATURAL_SORT_KEYS = ['software', 'province'];

  const sortedMachines = [...fleetFilteredMachines].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (sortConfig.key === 'accountState') {
      const aIdx = ACCOUNT_STATE_ORDER.indexOf((a.accountState || '').toLowerCase());
      const bIdx = ACCOUNT_STATE_ORDER.indexOf((b.accountState || '').toLowerCase());
      return sortConfig.direction === 'asc' ? aIdx - bIdx : bIdx - aIdx;
    }
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    if (NATURAL_SORT_KEYS.includes(sortConfig.key)) {
      if (aValue === '' && bValue !== '') return 1;
      if (bValue === '' && aValue !== '') return -1;
      const options = { numeric: true, sensitivity: 'base' };
      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue), undefined, options)
        : String(bValue).localeCompare(String(aValue), undefined, options);
    }
    return sortConfig.direction === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const filteredMachines = sortedMachines.filter((machine) =>
    [machine.name, machine.city, machine.province, machine.street, machine.id]
      .some(field => field.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  // Add this new function to generate CSV data
  const downloadCSV = () => {
    const headers = [
      'Account State',
      'Status',
      'Days Offline',
      'ID',
      'Name',
      'Street',
      'Postal Code',
      'City',
      'Province',
      'Today',
             'Model',
       'Plan',
       'Cash',
       'Mobile',
       'Total',
       'Days',
       'Avg'
    ];

    const csvData = filteredMachines.map(machine => [
      (machine.accountState || '').toLowerCase(),
      getStatusIndicator(machine.lastSeen),
      calculateDaysOffline(machine.lastSeen),
      machine.id.slice(0, 8),
      machine.name,
      `${machine.street} ${machine.housenumber}`,
      machine.postalcode,
      machine.city,
      machine.province,
      TODAY_STATUS_LABELS[normalizeTodayStatus(machine.todayStatus)],
             machine.type,
       getOvereenkomstLabel(machine.overeenkomst),
       calculateCash(machine),
       calculateMobile(machine),
       calculateRevenue(machine),
       calculateDaysSinceClear(machine.coinsLastEmptied),
       calculateAveragePerDay(machine)
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'machines.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="machine-manager-container">
      {/* DJB-182: per-device show/hide of the hideable columns, top-right;
          DJB-254 moved it above the search row. The choice persists in
          localStorage */}
      <div className="table-toolbar">
        <div className="customize-table" ref={customizeRef}>
          <button
            type="button"
            className="customize-table-btn"
            aria-haspopup="true"
            aria-expanded={customizeOpen}
            onClick={() => setCustomizeOpen((open) => !open)}
          >
            Customize Table
          </button>
          {customizeOpen && (
            <div className="customize-table-panel" role="group" aria-label="Show or hide columns">
              <p className="customize-table-panel-title">Columns</p>
              {HIDEABLE_COLUMNS.map(({ key, label }) => (
                <label key={key} className="customize-table-option">
                  <input
                    type="checkbox"
                    checked={isColumnVisible(key)}
                    onChange={() => toggleColumn(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
              <button type="button" className="customize-table-reset" onClick={resetColumns}>
                Reset to default
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="search-container">
        {/* DJB-255: wrapper carries the 'x' that clears the search term */}
        <div className="search-box-wrap">
          <input
            type="text"
            placeholder="Search"
            className="search-box"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm !== '' && (
            <button
              type="button"
              className="search-clear-btn"
              aria-label="Clear search"
              title="Clear search"
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>
        {/* DJB-254: wrapper so both filters stack under the search box on
            mobile; on desktop they stay beside it */}
        <div className="search-filters">
          <select
            className="fleet-select"
            aria-label="Fleet filter"
            value={fleetFilter}
            onChange={(e) => setFleetFilter(e.target.value)}
          >
            <option value="in-fleet">In fleet</option>
            <option value="total">Total fleet</option>
            <option value="out-fleet">Out fleet</option>
          </select>
          <select
            className="fleet-select"
            aria-label="Today filter"
            value={todayFilter}
            onChange={(e) => setTodayFilter(e.target.value)}
          >
            <option value="all">Today: all</option>
            {TODAY_STATUS_ORDER.map((status) => (
              <option key={status} value={status}>Today: {TODAY_STATUS_LABELS[status]}</option>
            ))}
          </select>
        </div>
      </div>
      {loading && <p className="loading">Loading machines...</p>}
      {error && <p className="error">Error: {error}</p>}
      <div className="table-container">
        {/* DJB-189: no-seen-col re-anchors the sticky Name column to the left
            edge when the (also sticky) Seen column is hidden */}
        <table className={`details-table accounts-list-table${isColumnVisible('seen') ? '' : ' no-seen-col'}`}>
          <thead>
            <tr>
              {isColumnVisible('seen') && <th className="col-seen" onClick={() => requestSort('daysOffline')}>Seen{getSortIndicator('daysOffline')}</th>}
              <th className="col-name" onClick={() => requestSort('name')}>Name{getSortIndicator('name')}</th>
              {isColumnVisible('street') && <th>Street</th>}
              {isColumnVisible('postalcode') && <th onClick={() => requestSort('postalcode')}>Post{getSortIndicator('postalcode')}</th>}
              {isColumnVisible('city') && <th onClick={() => requestSort('city')}>Place{getSortIndicator('city')}</th>}
              {/* DJB-206: Province column, hideable and sortable */}
              {isColumnVisible('province') && <th onClick={() => requestSort('province')}>Province{getSortIndicator('province')}</th>}
               {/* DJB-183: label renamed to Plan; the overeenkomst sort key /
                   API field stays */}
               <th onClick={() => requestSort('overeenkomst')}>Plan{getSortIndicator('overeenkomst')}</th>
               {isColumnVisible('state') && <th onClick={() => requestSort('accountState')}>State{getSortIndicator('accountState')}</th>}
               {isColumnVisible('today') && <th onClick={() => requestSort('todayStatus')}>Today{getSortIndicator('todayStatus')}</th>}
               {/* DJB-206: these six DJB-182 columns sort like the rest */}
               {isColumnVisible('id') && <th onClick={() => requestSort('id')}>ID{getSortIndicator('id')}</th>}
               {isColumnVisible('software') && <th onClick={() => requestSort('software')}>Software{getSortIndicator('software')}</th>}
               {isColumnVisible('type') && <th onClick={() => requestSort('type')}>Model{getSortIndicator('type')}</th>}
               {isColumnVisible('currentCredits') && <th onClick={() => requestSort('currentCredits')}>Credits{getSortIndicator('currentCredits')}</th>}
               {isColumnVisible('tokensLeft') && <th onClick={() => requestSort('tokensLeft')}>Tokens{getSortIndicator('tokensLeft')}</th>}
               {isColumnVisible('songsRequested') && <th onClick={() => requestSort('songsRequested')}>Songs{getSortIndicator('songsRequested')}</th>}
               {isColumnVisible('cash') && <th onClick={() => requestSort('cash')}>Cash{getSortIndicator('cash')}</th>}
               {isColumnVisible('mobile') && <th onClick={() => requestSort('mobile')}>Mobile{getSortIndicator('mobile')}</th>}
               {isColumnVisible('revenue') && <th onClick={() => requestSort('revenue')}>Total{getSortIndicator('revenue')}</th>}
               {isColumnVisible('daysSinceClear') && <th onClick={() => requestSort('daysSinceClear')}>Days{getSortIndicator('daysSinceClear')}</th>}
               {isColumnVisible('avgPerDay') && <th onClick={() => requestSort('avgPerDay')}>Avg{getSortIndicator('avgPerDay')}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredMachines.length > 0 ? (
              filteredMachines.map((machine) => {
                const seenStatus = getSeenStatus(machine.lastSeen);
                const todayStatus = normalizeTodayStatus(machine.todayStatus);
                return (
                <tr
                  key={machine.id}
                  onClick={() =>
                    setSelectedId(prev => (prev === machine.id ? null : machine.id))
                  }
                  className={selectedId === machine.id ? 'selected' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  {isColumnVisible('seen') && <td className={`center-data col-seen seen-${seenStatus}`}>{calculateDaysOffline(machine.lastSeen)}</td>}
                  {/* DJB-129: name takes the same online/offline color as the seen cell */}
                  <td className={`col-name seen-${seenStatus}`}>{machine.name}</td>
                  {isColumnVisible('street') && <td>{`${machine.street} ${machine.housenumber}`}</td>}
                  {isColumnVisible('postalcode') && <td className="center-data">{machine.postalcode}</td>}
                  {isColumnVisible('city') && <td>{machine.city}</td>}
                  {isColumnVisible('province') && <td>{machine.province}</td>}
                   <td className="center-data">{getOvereenkomstLabel(machine.overeenkomst)}</td>
                   {isColumnVisible('state') && <td className="center-data"><AccountStateBadge state={machine.accountState} /></td>}
                   {isColumnVisible('today') && <td className={`center-data today-status ${todayStatus}`}>{TODAY_STATUS_LABELS[todayStatus]}</td>}
                   {isColumnVisible('id') && <td className="center-data">{machine.id.slice(0, 8)}</td>}
                   {isColumnVisible('software') && <td className="center-data">{machine.softwareVersion || 'Unknown'}</td>}
                   {isColumnVisible('type') && <td className="center-data">{machine.type}</td>}
                   {isColumnVisible('currentCredits') && <td className="center-data">{machine.currentCredits || 0}</td>}
                   {isColumnVisible('tokensLeft') && <td className="center-data">{getTokensLeft(machine)}</td>}
                   {isColumnVisible('songsRequested') && <td className="center-data">{machine.songsRequestedSince || 0}</td>}
                   {isColumnVisible('cash') && <td className="center-data">{calculateCash(machine)}</td>}
                   {isColumnVisible('mobile') && <td className="center-data">{calculateMobile(machine)}</td>}
                   {isColumnVisible('revenue') && <td className="center-data">{calculateRevenue(machine)}</td>}
                   {isColumnVisible('daysSinceClear') && <td className="center-data">{calculateDaysSinceClear(machine.coinsLastEmptied)}</td>}
                   {isColumnVisible('avgPerDay') && <td className="center-data">{calculateAveragePerDay(machine)}</td>}
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={2 + HIDEABLE_COLUMNS.length - hiddenColumns.length}>No machines found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* DJB-130: the full detail view of the selected account, inline in the
          space below the table (DJB-128 moved the stat widgets to the
          Dashboard) */}
      <MachineDetails machine={selectedMachine} />

      <div className="csv-footer">
        <button
          className="download-csv-btn"
          onClick={downloadCSV}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Download CSV'}
        </button>
      </div>
    </div>
  );
};

export default MachineManager;
